import moment from 'moment';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '../constants';

export const MOMENT_BETWEEN_EXCLUDE_START = '(';
export const MOMENT_BETWEEN_EXCLUDE_END = ')';
export const MOMENT_BETWEEN_INCLUDE_START = '[';
export const MOMENT_BETWEEN_INCLUDE_END = ']';

export const EXCEL_DATE_FORMATS = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD',
  'DD-MMM-YY',
  'MMM D, YYYY',
  'D MMM YYYY',
  'D-MMM-YYYY',
  'YYYY/MM/DD',
  'MMM D YYYY',
  'DD MMM YY',
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YY-MM-DD',
  'DD/MM/YY',
  'MM/DD/YY',
  'MMMM D, YYYY',
  'D MMMM YYYY',
  'MMMM DD, YYYY',
  'DD MMMM YYYY',
  'DD.MM.YYYY',
  'D.M.YYYY',
  'YYYY.MM.DD',
  'MM.DD.YYYY',
  'YYYY-MM-DD HH:mm:ss',
  'MM/DD/YYYY HH:mm',
  'DD/MM/YYYY HH:mm:ss',
  'MMMM D YYYY HH:mm:ss',
  'D MMM YYYY HH:mm:ss',
  'ddd, MMM D, YYYY',
  'dddd, MMMM D, YYYY',
  'D MMM YY',
  'D MMM YYYY',
  'MMM D YY',
  'Do MMM YY',
  'Do MMM YYYY',
  'D MMMM YY',
  'D MMMM YYYY',
  'Do MMMM YY',
  'Do MMMM YYYY',
  'DD/MM/YYYY HH:mm:ss',
  'DD-MM-YYYY HH:mm:ss',
  'YYYY/MM/DD HH:mm:ss',
  'DD MMM YYYY HH:mm:ss'
];

export const getMomentBetweenIncludeString = (includeStart, includeEnd) => {
  return `${includeStart ? MOMENT_BETWEEN_INCLUDE_START : MOMENT_BETWEEN_EXCLUDE_START}${
    includeEnd ? MOMENT_BETWEEN_INCLUDE_END : MOMENT_BETWEEN_EXCLUDE_END
  }`;
};

export const getDurationFromRange = (startTime, endTime) => {
  const diff = moment(endTime).diff(moment(startTime));
  return getDurationFromMilliseconds(diff);
};

export const getMillisecondsFromDecimal = (decimalValue) => Math.round(decimalValue * 60) * 60 * 1000;

export const getDurationFromMilliseconds = (ms, allowNegative = false) => {
  const dur = moment.duration(allowNegative ? Math.abs(ms) : ms, 'milliseconds');
  let hours = Math.floor(dur.asHours());
  const mins = Math.floor(dur.asMinutes()) - hours * 60;
  hours = hours < 0 ? 24 + hours : hours;

  return `${allowNegative && ms < 0 ? '-' : ''}${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`;
};

export const isEndTimeBeforeStartTime = (startTime, endTime, includeEqualStartEndTIme = true) =>
  endTime.get('hour') < startTime.get('hour') ||
  (endTime.get('hour') === startTime.get('hour') &&
    (includeEqualStartEndTIme
      ? endTime.get('minute') <= startTime.get('minute')
      : endTime.get('minute') < startTime.get('minute')));

// update start time and end time with the correct date
export const getTimeRangeWithDates = ({
  date,
  startTime,
  endTime,
  checkBaseStartDate = false,
  // consider 10:00 start time and 10:00 end time as in different dates
  includeEqualStartEndTIme = true
}) => {
  const startDate = moment(date);
  const endDate = moment(date);
  const endDateWithExtraDay = moment(date);

  const startMoment = moment(startTime);
  const endMoment = moment(endTime);

  const start = startMoment.isValid()
    ? startDate.set({
        hour: startMoment.get('hour'),
        minute: startMoment.get('minute'),
        seconds: 0
      })
    : null;

  // get the endTime after adding a day
  const endTimeWithExtraDay =
    endTime && endMoment.isValid()
      ? endDateWithExtraDay.set({
          date: endDateWithExtraDay.add(1, 'day').get('date'),
          hour: endMoment.get('hour') || '',
          minute: endMoment.get('minute') || '',
          seconds: 0
        })
      : null;

  // get the endTime withouth adding a day
  const endTimeWithoutExtraDay =
    endTime && endMoment.isValid()
      ? endDate.set({
          date: endDate.get('date'),
          hour: endMoment.get('hour') || '',
          minute: endMoment.get('minute') || '',
          seconds: 0
        })
      : null;

  const endTimeBeforeStartTime = isEndTimeBeforeStartTime(startMoment, endMoment, includeEqualStartEndTIme);
  let addExtraDay = endTimeBeforeStartTime;

  /* conditions:
    1. startTime date is before the event date.
    2. endTime is before startTime (meaning there is a need to add extra day) 
    3. endTimeWithExtraDay is not null
  */
  const calaculateIfExtraDay =
    checkBaseStartDate && start && moment(startTime).isBefore(start) && endTimeBeforeStartTime && endTimeWithExtraDay;

  if (calaculateIfExtraDay) {
    //endTime needs to be +1 only if the total durtion after the addition does not exceeds 24 hours
    addExtraDay = !(moment.duration(endTimeWithExtraDay.diff(startTime, 'milliseconds')).asHours() > 24);
  }

  const end = addExtraDay ? endTimeWithExtraDay : endTimeWithoutExtraDay;

  return {
    startTime: start ? start.format(API_DATE_TIME_FORMAT) : null,
    endTime: end ? end.format(API_DATE_TIME_FORMAT) : null
  };
};

export const getDaysOfWeek = (startFrom, totalDays = 7) => {
  const startDate = moment(startFrom).startOf('week').startOf('day').subtract(1, 'day');

  return [...Array(totalDays)].map(() => {
    startDate.add(1, 'day');
    return {
      name: startDate.format('dddd').toLowerCase(),
      nameCapital: startDate.format('dddd'),
      day: startDate.format('ddd'),
      date: startDate.format('YYYY-MM-DD'),
      index: startDate.get('day') + 1,
      momentDate: startDate.clone()
    };
  });
};

export const getDateByDay = (startFrom) => {
  const startMoment = moment(startFrom).startOf('day');
  const startDayOfWeek = startMoment.day(); // 0=Sunday, locale-independent

  return [...Array(7)].reduce((all, _, day) => {
    const offset = (day - startDayOfWeek + 7) % 7;
    all[day] = startMoment.clone().add(offset, 'days').format(API_DATE_FORMAT);
    return all;
  }, {});
};

export const ago = (date, t) => {
  const MINUTE = 60;
  const HOUR = MINUTE * 60;
  const DAY = HOUR * 24;
  const WEEK = DAY * 7;
  const YEAR = WEEK * 52;

  const seconds = moment().diff(moment.utc(date), 'seconds');

  // const date = moment(value).format(`${dateFormat} ${timeFormat}`);

  if (seconds < MINUTE) {
    return t('aFewSecondsAgo');
  }

  const minutes = Math.floor(seconds / 60);
  if (seconds < HOUR) {
    return t('minutesAgo', { value: minutes });
  }

  const hours = Math.floor(minutes / 60);
  if (seconds < DAY) {
    return t('hoursAgo', { value: hours });
  }

  const days = Math.floor(hours / 24);
  if (seconds < WEEK) {
    return t('daysAgo', { value: days });
  }

  const weeks = Math.floor(days / 7);
  if (seconds < YEAR) {
    return t('weeksAgo', { value: weeks });
  }

  const years = Math.floor(weeks / 52);
  return t('yearsAgo', { value: years });
};

/**
 * Returns a human-readable "X from now" string for a future date (e.g. snoozed until).
 * @param {string|Date} date - Future date
 * @param {Function} t - i18n function for locale keys (e.g. hoursFromNow, daysFromNow)
 * @returns {string|null} Formatted string or null if date is in the past
 */
export const fromNow = (date, t) => {
  if (!date) return null;
  const until = moment.utc(date);
  if (!until.isValid() || until.isSameOrBefore(moment())) return null;
  const diffMs = until.diff(moment());
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(weeks / 4);
  if (hours < 1) return t('help_desk_less_than_one_hour_from_now');
  if (hours === 1) return t('help_desk_one_hour_from_now');
  if (hours < 24) return t('help_desk_hours_from_now', { value: hours });
  if (days === 1) return t('help_desk_one_day_from_now');
  if (days < 14) return t('help_desk_days_from_now', { value: days });
  if (weeks === 1) return t('help_desk_one_week_from_now');
  if (weeks < 9) return t('help_desk_weeks_from_now', { value: weeks });
  if (months === 1) return t('help_desk_one_month_from_now');
  return t('help_desk_months_from_now', { value: months });
};

export const convertTimeStringToMoment = (time) => (time ? moment(`2000-12-31 ${time}`) : moment());

export const DEFAULT_START_TIME = '09:00:00';

export const DEFAULT_END_TIME = '17:00:00';

export const generateRange = ({ start, end, step = 'day', granularity = 'day', useUTC = true }) => {
  let pointer = useUTC ? moment.utc(start) : moment(start);
  const last = useUTC ? moment.utc(end) : moment(end);

  const dates = [];

  while (pointer.isSameOrBefore(last, granularity)) {
    const curr = useUTC ? moment.utc(pointer) : moment(pointer);

    dates.push(curr);
    pointer = pointer.add(1, step);
  }

  return dates;
};

export const roundTimeToNearest = ({ inputTime, roundTimeInMin, format }) => {
  const momentDate = moment(inputTime, format ?? API_DATE_TIME_FORMAT);

  if (roundTimeInMin) {
    const threshold = roundTimeInMin / 2;

    const remainder = momentDate.minutes() % roundTimeInMin;

    // rounding down
    if (remainder < threshold) {
      momentDate.subtract(remainder, 'minutes');
    } else {
      // rounding up
      momentDate.add(roundTimeInMin - remainder, 'minutes');
    }
    momentDate.set({ second: 0, millisecond: 0 });
  }

  return momentDate;
};

export const getTotalDays = (startDate, endDate) => {
  return moment(endDate).diff(moment(startDate), 'days') + 1;
};

export const areRangesOverlapping = (timeRanges) => {
  for (let i = 0; i < timeRanges.length - 1; i++) {
    if (timeRanges[i].end.isAfter(timeRanges[i + 1].start)) {
      return true; // Overlap found
    }
  }
  return false; // No overlap
};

export const formatNumberToMinutes = (minutes, type) => {
  switch (type) {
    case 'minutes':
      return minutes;
    case 'hours':
      return minutes * 60;
    default:
      return minutes * 60 * 24;
  }
};

export const formatMinutesToString = (minutes, t) => {
  if (minutes < 60) {
    return t ? `${minutes} ${t('minutes')}` : `${minutes} minutes`;
  }
  if (minutes < 60 * 24) {
    return t ? `${minutes / 60} ${t('hours')}` : `${minutes / 60} hours`;
  }
  return t ? `${minutes / 60 / 24} ${t('days')}` : `${minutes / 60 / 24} days`;
};

export const isDateInRange = ({ date, startDate, endDate, includeStart = true, includeEnd = true }) => {
  const dateMoment = moment(date, API_DATE_FORMAT);
  const startRangeMoment = moment(startDate, API_DATE_FORMAT);
  const endRangeMoment = moment(endDate, API_DATE_FORMAT);
  const momentInclude = getMomentBetweenIncludeString(includeStart, includeEnd);

  return dateMoment.isBetween(startRangeMoment, endRangeMoment, 'day', momentInclude);
};

/**
 * Returns "this bi-weekly" and "last bi-weekly" date ranges anchored to paymentStartDate.
 *
 * Algorithm:
 *   daysFromAnchor = today − paymentStartDate  (full days, can be negative)
 *   periodsElapsed = Math.floor(daysFromAnchor / 14)
 *   thisBiWeeklyStart = paymentStartDate + periodsElapsed × 14 days
 *   thisBiWeeklyEnd   = thisBiWeeklyStart + 13 days  (inclusive, so 14 days total)
 *   lastBiWeeklyStart = thisBiWeeklyStart − 14 days
 *   lastBiWeeklyEnd   = thisBiWeeklyStart − 1 day
 *
 * @param {string|moment.Moment} paymentStartDate - first day of the first bi-weekly period
 * @param {moment.Moment} [today] - override for "today" (defaults to moment())
 */
export const getBiWeeklyRange = (paymentStartDate, today = moment()) => {
  const anchor = moment(paymentStartDate).startOf('day');
  const now = moment(today).startOf('day');

  const daysFromAnchor = now.diff(anchor, 'days');
  const periodsElapsed = Math.floor(daysFromAnchor / 14);

  const thisBiWeeklyStart = anchor
    .clone()
    .add(periodsElapsed * 14, 'days')
    .startOf('day');
  const thisBiWeeklyEnd = thisBiWeeklyStart.clone().add(13, 'days').endOf('day');

  const lastBiWeeklyStart = thisBiWeeklyStart.clone().subtract(14, 'days').startOf('day');
  const lastBiWeeklyEnd = thisBiWeeklyStart.clone().subtract(1, 'day').endOf('day');

  return {
    thisBiWeekly: { start: thisBiWeeklyStart, end: thisBiWeeklyEnd },
    lastBiWeekly: { start: lastBiWeeklyStart, end: lastBiWeeklyEnd }
  };
};
